import { useEffect, useState } from "react";
import { Gift } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import type { ZemelapisGiftFields } from "@/lib/zemelapisTiers";

export type GiftTier = "gift_single" | "gift_pair" | "karjera_gift";

interface GiftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier: GiftTier;
  /** Prefilled "Nuo ko" value, e.g. the buyer's own name. */
  defaultSenderName?: string;
  onConfirm: (fields: ZemelapisGiftFields) => void;
}

const NON_ASCII = /[^\x00-\x7F]/;
const GIFT_COPY: Record<GiftTier, { title: string; description: string; price: string }> = {
  gift_pair: {
    title: "Žemėlapis — sau ir artimam",
    description: "Vienas žemėlapis lieka tau, kitas tampa dovana artimam žmogui. Pridėk asmeninę žinutę.",
    price: "29.90",
  },
  gift_single: {
    title: "Žemėlapis — dovana",
    description: "Pridėk asmeninę žinutę — ji bus matoma atrakinant kodą.",
    price: "19.90",
  },
  karjera_gift: {
    title: "Karjeros žemėlapis — dovana",
    description: "Pridėk asmeninę žinutę — ji bus matoma aktyvuojant Karjeros žemėlapio kodą.",
    price: "24.90",
  },
};
const LT_LETTERS_ERROR =
  "El. pašto adrese negali būti lietuviškų raidžių. Patikrinkite adresą — greičiausiai vietoj „š“ turi būti „s“, vietoj „ė“ — „e“.";

/** Gift personalisation dialog shown before any Žemėlapis gift purchase. */
const GiftDialog = ({ open, onOpenChange, tier, defaultSenderName, onConfirm }: GiftDialogProps) => {
  const { toast } = useToast();
  const copy = GIFT_COPY[tier];
  const [senderName, setSenderName] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [message, setMessage] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (defaultSenderName) setSenderName((prev) => prev || defaultSenderName);
      return;
    }
    // Reset every field when the dialog closes so the next gift starts clean.
    setSenderName("");
    setRecipientName("");
    setRecipientEmail("");
    setMessage("");
    setEmailError(null);
  }, [open, defaultSenderName]);

  const handleConfirm = () => {
    const email = recipientEmail.trim();
    if (email && NON_ASCII.test(email)) {
      setEmailError(LT_LETTERS_ERROR);
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({
        title: "Netinkamas el. paštas",
        description: "Patikrink el. pašto adresą arba palik lauką tuščią.",
        variant: "destructive",
      });
      return;
    }
    setEmailError(null);
    onOpenChange(false);
    onConfirm({
      senderName: senderName.trim() || undefined,
      recipientName: recipientName.trim() || undefined,
      recipientEmail: email || undefined,
      message: message.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream border-ink/15 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {copy.title}
          </DialogTitle>
          <DialogDescription className="text-ink/70">
            {copy.description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="gift-sender" className="text-xs tracking-widest uppercase text-ink/60">Nuo ko (neprivaloma)</Label>
            <Input
              id="gift-sender"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value.slice(0, 80))}
              placeholder="Tavo vardas"
              className="bg-white/70 border-ink/20"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gift-recipient" className="text-xs tracking-widest uppercase text-ink/60">Kam (neprivaloma)</Label>
            <Input
              id="gift-recipient"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value.slice(0, 80))}
              placeholder="Vardas"
              className="bg-white/70 border-ink/20"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gift-recipient-email" className="text-xs tracking-widest uppercase text-ink/60">El. paštas (neprivaloma)</Label>
            <Input
              id="gift-recipient-email"
              type="email"
              value={recipientEmail}
              onChange={(e) => {
                const v = e.target.value.slice(0, 200);
                setRecipientEmail(v);
                setEmailError(NON_ASCII.test(v) ? LT_LETTERS_ERROR : null);
              }}
              placeholder="vardas@pastas.lt"
              aria-invalid={emailError ? true : undefined}
              className="bg-white/70 border-ink/20"
            />
            {emailError ? (
              <p className="text-[11px] text-destructive">{emailError}</p>
            ) : (
              <p className="text-[11px] text-ink/55">Jei įvesi — kodą išsiųsime ir šiuo adresu. Kopija liks tavo paskyroje ir el. pašte.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gift-message" className="text-xs tracking-widest uppercase text-ink/60">Žinutė (neprivaloma)</Label>
            <Textarea
              id="gift-message"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
              placeholder="Pora šiltų sakinių artimam…"
              rows={4}
              className="bg-white/70 border-ink/20 resize-none"
            />
            <p className="text-[11px] text-ink/50 text-right">{message.length}/1000</p>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-ink/30">
            Atšaukti
          </Button>
          <Button onClick={handleConfirm} className="bg-ink hover:bg-ink/85 text-cream rounded-full">
            <Gift className="mr-2 h-4 w-4" /> Toliau — €{copy.price}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GiftDialog;
